import { Button as DxButton } from 'devextreme-react/button';
import { Column, Summary, ToolbarItem } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { ButtonOnExporting } from '../../../utils/DataGridExporter';
import useFormatMessage from '../../../utils/useFormatMessage';
import BolgeIslemleri from './BolgeIslemleri';

const EXCLUDED_WAREHOUSES = ['GEMİ', 'SANAL', 'FABRİKA'];
const EXCLUDED_ZONES = ['SANAL'];
const EXCLUDED_TYPES = [];

const INITIAL_ZONE = {
    id: null,
    zone_code: '',
    zone_name: '',
    capacity: 0,
    is_shipping_zone: false,
    requires_form_on_exit: false,
    is_active: true,
    warehouse_id: 0,
    create_date: null
};

// Memoize edilmiş alt bileşenler
const MemoizedBreadcrumb = React.memo(Breadcrumb);
const MemoizedBolgeIslemleri = React.memo(BolgeIslemleri);

const StatusIcon = React.memo(({ isActive }) => (
    <i
        className={`fa fa-${isActive ? 'check' : 'times'}`}
        style={{ color: isActive ? 'green' : 'red' }}
    />
));

const ActionButton = React.memo(({ data, onEdit }) => {
    const { hasRole } = useAuth(3);

    if (!hasRole('bolge_role_update')) return null;

    return (
        <i
            className="fa fa-edit ml-2"
            onClick={() => onEdit(data)}
            style={{
                cursor: 'pointer',
                color: 'blue',
                fontSize: '18px'
            }}
        />
    );
});

const BolgeTanim = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(3);
    const isMounted = useRef(true);
    const gridRef = useRef(null);

    const [isOpen, setIsOpen] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [dataSource, setDataSource] = React.useState([]);
    const [trigger, setTrigger] = React.useState(false);
    const [selected, setSelected] = React.useState(INITIAL_ZONE);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchZones = useCallback(async () => {
        if (!isMounted.current || isLoading) return;

        setIsLoading(true);
        if (gridRef.current) {
            gridRef.current.instance.beginCustomLoading(t('Yükleniyor'));
        }

        try {
            const res = await api.post('/zone/getAll');
            if (!isMounted.current) return;

            const allData = res.data.data || [];
            const filteredData = allData.filter(item => (
                !EXCLUDED_WAREHOUSES.includes(item.warehouse_name) &&
                !EXCLUDED_ZONES.includes(item.zone_name) &&
                !EXCLUDED_TYPES.includes(item.zone_type)
            ));

            setDataSource(filteredData);
        } catch (err) {
            console.error('Zone fetch error:', err);
            if (isMounted.current) {
                toast.error(t('Bölge Listesi Alınırken hata oluştu'));
                setDataSource([]);
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
                if (gridRef.current) {
                    gridRef.current.instance.endCustomLoading();
                }
            }
        }
    }, []);

    React.useEffect(() => {
        fetchZones();
    }, [trigger, fetchZones]);

    const handleDelete = useCallback(async () => {
        try {
            const employeeId = parseInt(Buffer.from(localStorage.getItem('employee_id'), 'base64').toString('ascii'));
            await api.post('/zone/delete', {
                id: selected.id,
                update_user_id: employeeId
            });

            if (!isMounted.current) return;

            toast.success(t('Bölge Silme Başarılı'));
            setDeleteModal(false);
            setTrigger(prev => !prev);
        } catch (err) {
            if (!isMounted.current) return;

            const errorMessage = err.response?.data?.error?.split(':');
            if (errorMessage) {
                const message = t('Bölge Silme Başarısız') + ': ' + t(errorMessage[0]);
                toast.error(errorMessage[1] ? `${message} ID: ${t(errorMessage[1])}` : message);
                return;
            }
            toast.error(t('Bölge Silme Başarısız'));
        }
    }, [selected.id]);

    const createZoneCode = useCallback((num) => {
        return `B${String(num).padStart(8, '0')}`;
    }, []);

    const handleAddNew = useCallback(() => {
        const nextCode = dataSource?.length === 0
            ? 1
            : parseInt(dataSource[0].zone_code.replace('B', '')) + 1;

        setSelected({
            ...INITIAL_ZONE,
            zone_code: createZoneCode(nextCode)
        });
        setIsOpen(true);
    }, [dataSource, createZoneCode]);

    const handleRefresh = useCallback(() => {
        if (!isLoading) {
            setTrigger(prev => !prev);
        }
    }, [isLoading]);

    const handleExport = useCallback((e) => {
        ButtonOnExporting(
            e,
            gridRef,
            `Bölge Listesi - ${moment(new Date()).format('DD/MM/YYYY')}`,
            'Sayafa-1'
        );
    }, []);

    const handleEdit = useCallback((data) => {
        setSelected(data);
        setIsOpen(true);
    }, []);

    const toggleDeleteModal = useCallback(() => setDeleteModal(prev => !prev), []);

    const columns = useMemo(() => [
        {
            caption: t('Bölge Kodu'),
            dataField: 'zone_code',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 140,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Depo Adı'),
            dataField: 'warehouse_name',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 200,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Bölge Adı'),
            dataField: 'zone_name',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 200,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Bölge Açıklaması'),
            dataField: 'zone_description',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 200,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Kapasite'),
            dataField: 'capacity',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 120,
            fixed: true,
            allowResizing: false
        },
        {
            caption: t('Geçerli'),
            dataField: 'is_active',
            alignment: 'center',
            selectedFilterOperation: 'contains',
            width: 100,
            fixed: true,
            allowResizing: false,
            cellRender: ({ data }) => <StatusIcon isActive={data.is_active} />
        },
        {
            caption: t('İşlem'),
            alignment: 'center',
            width: 80,
            fixed: true,
            allowResizing: false,
            allowExporting: false,
            cellRender: ({ data }) => <ActionButton data={data} onEdit={handleEdit} />
        }
    ], [t, handleEdit]);

    const ActionButtons = () => {
        return (
            <div className="card-toolbar d-flex justify-content-end align-items-center mb-0"
                style={{ gap: '5px' }}
            >
                <DxButton
                    stylingMode="outlined"
                    type="default"
                    onClick={handleRefresh}
                    disabled={isLoading}
                    text=""
                    width={38}
                    height={38}
                >
                    <i className="fa fa-refresh" />
                </DxButton>

                {hasRole('bolge_role_write') && (
                    <DxButton
                        type="default"
                        stylingMode="contained"
                        onClick={handleAddNew}
                        disabled={isLoading}
                        width={90}
                        height={38}
                    >
                        <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-plus" />&nbsp;
                            <span className="ms-2">{t('Ekle')}</span>
                        </div>
                    </DxButton>
                )}

                {hasRole('bolge_role_export') && (
                    <DxButton
                        stylingMode="contained"
                        type="success"
                        onClick={handleExport}
                        disabled={isLoading}
                        width={38}
                        height={38}
                        title={t('Excele Aktar')}
                    >
                        <i className="fa fa-file-excel-o" />
                    </DxButton>
                )}
            </div>
        );
    };

    return (
        <>
            <MemoizedBreadcrumb current={t('Bölge Tanım')} />
            <Card className="p-4">
                <ActionButtons />
                <Row>
                    <DatagridCustom
                        dataSource={dataSource}
                        model="bolge-tanim"
                        reff={gridRef}
                    >
                        {columns.map(column => (
                            <Column key={column.dataField || column.caption} {...column} />
                        ))}

                        <Summary>
                            <ToolbarItem
                                name="toplam"
                                location="after"
                                text={t('Toplam')}
                            />
                        </Summary>
                    </DatagridCustom>
                </Row>
            </Card>

            <MemoizedBolgeIslemleri
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setTrigger={setTrigger}
                trigger={trigger}
                selected={selected}
            />

            <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>
                    {t('Bölge Silme Formu')}
                </ModalHeader>
                <ModalBody>
                    {t('Bölge Kodu')}: <b>{selected?.zone_code}</b> <br />
                    {t('Bölge Silme İşlemi Yapmak İstediğinize Emin Misiniz?')}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={handleDelete}>
                        {t('Sil')}
                    </Button>
                    <Button color="secondary" onClick={toggleDeleteModal}>
                        {t('İptal')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default React.memo(BolgeTanim);